import { useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Select, SelectItem } from '@carbon/react';
import { vnDayKey, timeZoneLabel } from '@ruby-cms/shared';
import { api } from '../api/client';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { FilterBar, useUrlFilters } from '../components/Filters';
import {
  GRAINS, GRAIN_LABEL, GRAIN_LETTER, GrainSwitch, Hours, LineChart, Num, Panel, Rate, SCOPE_KEYS, periodLabel, periodOf, periodShift,
  scopeFields, useOptions, type Grain,
} from '../components/Analytics';
import { compactVnd } from '../components/format';

// Screen 5 of docs/cms-golive/kpi-catalogue.md: real players grouped by the day, week or month of
// their registration or first deposit. Retention matrix (D0..D30, W0..W12, M0..M6), the cumulative
// value per member on the same grid (LTV) and the first-deposit conversion of registration cohorts.

type Cell = { n: number; active: number; pct: number | null; running: boolean };
type LtvCell = { n: number; total: number; perMember: number | null; running: boolean };
type Avg = { n: number; cohorts: number; size: number; value: number; avg: number | null };
type Window = { days: number; n: number; matured: number; pct: number | null };
type Ftd = { within: Window[]; toDate: { n: number; pct: number | null }; medianHours?: number | null };
type Row = { cohort: string; size: number; retention: Cell[]; ltv: LtvCell[]; ftd: Ftd | null };
type Cohorts = {
  from: string; to: string; basis: 'reg' | 'ftd'; grain: Grain; activity: 'bet' | 'deposit' | 'login'; measure: 'ngr' | 'ggr' | 'deposit';
  today: string; columns: number; rows: Row[]; average: { retention: Avg[]; ltv: Avg[] }; total: { size: number; ftd: Ftd | null };
};

// the default range of each grain (as the API): the oldest cohort already has a finished D30, W12 or M6
const SHOWN: Record<Grain, number> = { day: 32, week: 14, month: 8 };
const defaultFrom = (to: string, g: Grain) => periodShift(periodOf(to, g), -(SHOWN[g] - 1), g);

const ACTIVITY: Record<Cohorts['activity'], string> = { bet: 'có đặt cược', deposit: 'có nạp tiền', login: 'có đăng nhập' };
const MEASURE: Record<Cohorts['measure'], string> = { ngr: 'NGR', ggr: 'GGR', deposit: 'Tiền nạp' };
const PERIOD: Record<Grain, string> = { day: 'ngày', week: 'tuần', month: 'tháng' };

const pctText = (v: number) => `${(v * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`;
const heat = (v: number | null): CSSProperties => ({ '--p': v === null ? 0 : Math.max(0, Math.min(1, v)) } as CSSProperties);

function RetCell({ c, size }: { c: Cell | undefined; size: number }) {
  if (!c || !size) return <td className="mx__c mx__c--none" />;
  return (
    <td className={'mx__c' + (c.running ? ' mx__c--run' : '')} style={heat(c.pct)}
      title={`${c.active} / ${size} người${c.running ? ' (kỳ đang chạy)' : ''}`}>
      {pctText(c.pct ?? 0)}{c.running ? '*' : ''}
    </td>
  );
}

function LtvTd({ c }: { c: LtvCell | undefined }) {
  if (!c || c.perMember === null) return <td className="mx__c mx__c--none" />;
  return (
    <td className={'mx__c mx__c--money' + (c.running ? ' mx__c--run' : '') + (c.perMember < 0 ? ' num--neg' : '')}
      title={`Tổng ₩${Math.round(c.total).toLocaleString('vi-VN')}${c.running ? ' (kỳ đang chạy)' : ''}`}>
      {'₩' + compactVnd(Math.round(c.perMember))}{c.running ? '*' : ''}
    </td>
  );
}

function WindowTd({ w }: { w: Window }) {
  if (!w.matured) return <td className="r muted" title="Chưa ai trong nhóm hết khoảng này">-</td>;
  return <td className="r" title={`${w.n} / ${w.matured} người đã hết khoảng`}><Rate v={w.pct} /> <span className="muted">({w.n}/{w.matured})</span></td>;
}

export function StatsCohortsPage() {
  const o = useOptions();
  const [params] = useSearchParams();
  const today = vnDayKey(new Date());
  const grainParam = params.get('grain');
  const grain: Grain = (GRAINS as readonly string[]).includes(grainParam ?? '') ? (grainParam as Grain) : 'day';
  const f = useUrlFilters([...SCOPE_KEYS, 'basis', 'grain', 'activity', 'measure'], {
    defaults: { from: defaultFrom(today, grain), to: today, basis: 'reg', grain: 'day', activity: 'bet', measure: 'ngr' },
  });
  const [section, setSection] = useState('retention');
  const q = useQuery({ queryKey: ['analytics', 'cohorts', f.query], queryFn: () => api.get<Cohorts>('/api/admin/analytics/cohorts', f.query) });
  const d = q.data;
  const fields = [
    { key: 'basis', label: 'Nhóm theo', kind: 'select' as const, options: [['reg', 'Ngày đăng ký'], ['ftd', 'Ngày nạp lần đầu']] as const },
    { key: 'activity', label: 'Giữ chân là', kind: 'select' as const, options: [['bet', 'Có đặt cược'], ['deposit', 'Có nạp tiền'], ['login', 'Có đăng nhập']] as const },
    { key: 'measure', label: 'Giá trị (LTV)', kind: 'select' as const, options: [['ngr', 'NGR (lãi ròng)'], ['ggr', 'GGR (lãi nhà cái)'], ['deposit', 'Tiền nạp']] as const },
    ...scopeFields(o.data, { game: true }),
  ];
  const setGrain = (g: Grain) => f.set({ ...f.values, grain: g, from: defaultFrom(f.values.to || today, g) });
  const letter = GRAIN_LETTER[d?.grain ?? grain];
  const cols = d ? Array.from({ length: d.columns + 1 }, (_, n) => n) : [];
  const rows = d ? [...d.rows].reverse() : [];
  const sections: [string, string][] = [['retention', 'Giữ chân'], ['ltv', 'Giá trị (LTV)'], ...(d?.basis === 'reg' ? [['ftd', 'Chuyển đổi nạp đầu'] as [string, string]] : [])];
  // curves: the finished-cell average and the newest cohorts with at least two finished cells (one
  // point draws no line); a running period is never drawn (a part of a period would read as a finished rate)
  const recent = d ? d.rows.filter((r) => r.size && r.retention.filter((c) => !c.running).length >= 2).slice(-3) : [];
  const upTo = d ? Math.max(1, ...d.average.retention.filter((a) => a.cohorts).map((a) => a.n + 1), ...recent.map((r) => r.retention.filter((c) => !c.running).length)) : 0;
  const done = <T extends { running: boolean }>(c: T | undefined) => (c && !c.running ? c : undefined);
  // the average row has no single headcount: each average cell counts only the cohorts that finished it
  const avgSize = <td className="mx__n muted" title="Mỗi ô trung bình chỉ tính các nhóm đã trọn kỳ đó (rê chuột vào ô để xem số người)">-</td>;
  const labels = Array.from({ length: upTo }, (_, n) => `${letter}${n}`);

  return (
    <div className="page">
      <PageHeader title="Nhóm thuần tập và giữ chân" lead={`Người chơi thật chia theo ngày, tuần (thứ Hai đến Chủ nhật) hoặc tháng đăng ký hoặc nạp lần đầu, ${timeZoneLabel()}.`} />
      <GrainSwitch value={grain} onChange={setGrain} />
      <FilterBar id="co" fields={fields} filters={f} exportPath="/api/admin/analytics/cohorts" exportQuery={{ section: sections.some(([k]) => k === section) ? section : 'retention' }}>
        <Select id="co-section" labelText="Bảng xuất" size="md" value={section} onChange={(e) => setSection(e.target.value)}>
          {sections.map(([k, t]) => <SelectItem key={k} value={k} text={t} />)}
        </Select>
      </FilterBar>
      {q.isLoading ? <BlockSkeleton lines={12} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d?.total.size ? (
        <EmptyState title="Không có người chơi nào vào nhóm trong khoảng này" body="Chọn khoảng ngày dài hơn hoặc bỏ lọc." />
      ) : (
        <>
          <Panel title={`Tỷ lệ giữ chân theo ${PERIOD[d.grain]}: người ${ACTIVITY[d.activity]} (${d.total.size.toLocaleString('vi-VN')} người, ${d.rows.length} nhóm)`}>
            <div className="table-scroll">
              <table className={'mx' + (cols.length > 13 ? ' mx--dense' : '')}>
                <thead><tr><th className="mx__h">Nhóm ({d.basis === 'reg' ? 'đăng ký' : 'nạp đầu'})</th><th className="mx__n">Số người</th>{cols.map((n) => <th key={n}>{letter}{n}</th>)}</tr></thead>
                <tbody>
                  <tr className="mx__avg">
                    <td className="mx__h">Trung bình</td>{avgSize}
                    {cols.map((n) => { const a = d.average.retention[n]!; return a.avg === null ? <td key={n} className="mx__c mx__c--none" /> : <td key={n} className="mx__c" style={heat(a.avg)} title={`${a.value} / ${a.size} người, ${a.cohorts} nhóm đã trọn kỳ này`}>{pctText(a.avg)}</td>; })}
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.cohort}>
                      <td className="mx__h">{periodLabel(r.cohort, d.grain)}</td><td className="mx__n"><Num v={r.size} /></td>
                      {cols.map((n) => <RetCell key={n} c={r.retention[n]} size={r.size} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted">
              {letter}n = phần trăm người trong nhóm {ACTIVITY[d.activity]} trong {PERIOD[d.grain]} thứ n kể từ {PERIOD[d.grain]} vào nhóm ({letter}0 = chính {PERIOD[d.grain]} vào nhóm, chỉ tính từ ngày vào nhóm của từng người).
              Ô có dấu * là kỳ đang chạy, chưa hết. Dòng Trung bình và các đường chỉ lấy các ô đã hết kỳ; Trung bình tính theo số người.
            </p>
            {upTo > 1 ? (
              <LineChart title="Đường giữ chân" labels={labels} format={(v) => pctText(v)}
                series={[
                  { label: 'Trung bình', points: labels.map((_, n) => d.average.retention[n]?.avg ?? null) },
                  ...recent.map((r) => ({ label: periodLabel(r.cohort, d.grain), points: labels.map((_, n) => done(r.retention[n])?.pct ?? null) })),
                ]} />
            ) : null}
          </Panel>

          <Panel title={`${MEASURE[d.measure]} cộng dồn mỗi người (LTV) theo ${PERIOD[d.grain]} (${d.total.size.toLocaleString('vi-VN')} người, ${d.rows.length} nhóm)`}>
            <div className="table-scroll">
              <table className={'mx' + (cols.length > 13 ? ' mx--dense' : '')}>
                <thead><tr><th className="mx__h">Nhóm</th><th className="mx__n">Số người</th>{cols.map((n) => <th key={n}>{letter}{n}</th>)}</tr></thead>
                <tbody>
                  <tr className="mx__avg">
                    <td className="mx__h">Trung bình</td>{avgSize}
                    {cols.map((n) => { const a = d.average.ltv[n]!; return a.avg === null ? <td key={n} className="mx__c mx__c--none" /> : <td key={n} className={'mx__c mx__c--money' + (a.avg < 0 ? ' num--neg' : '')} title={`${a.size} người trong ${a.cohorts} nhóm đã trọn kỳ này`}>{'₩' + compactVnd(Math.round(a.avg))}</td>; })}
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.cohort}>
                      <td className="mx__h">{periodLabel(r.cohort, d.grain)}</td><td className="mx__n"><Num v={r.size} /></td>
                      {cols.map((n) => <LtvTd key={n} c={r.ltv[n]} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted">
              Mỗi ô = tổng {MEASURE[d.measure]} của cả nhóm từ ngày vào nhóm đến hết {PERIOD[d.grain]} đó, chia cho số người trong nhóm. Ô có dấu * là kỳ đang chạy.
              {d.measure === 'ngr' ? ' NGR = GGR - thưởng - hoa hồng đại lý.' : ''}
              {d.measure === 'ggr' && f.values.measure === 'ngr' ? ' Đang lọc theo game nên đo bằng GGR của game đó (thưởng và hoa hồng không chia theo game).' : ''}
            </p>
            {upTo > 1 ? (
              <LineChart title="Đường LTV" labels={labels} format={(v) => '₩' + compactVnd(Math.round(v))}
                series={[
                  { label: 'Trung bình', points: labels.map((_, n) => d.average.ltv[n]?.avg ?? null) },
                  ...recent.map((r) => ({ label: periodLabel(r.cohort, d.grain), points: labels.map((_, n) => done(r.ltv[n])?.perMember ?? null) })),
                ]} />
            ) : null}
          </Panel>

          {d.basis === 'reg' && d.total.ftd ? (
            <Panel title="Chuyển đổi nạp lần đầu của nhóm đăng ký">
              <div className="table-scroll">
                <table className="plain">
                  <thead><tr><th>Nhóm</th><th className="r">Số người</th><th className="r">Nạp ngay ngày đầu</th><th className="r">Trong 7 ngày đầu</th><th className="r">Trong 30 ngày đầu</th><th className="r">Đã nạp đến nay</th><th className="r">Đăng ký đến nạp (trung vị)</th></tr></thead>
                  <tbody>{rows.filter((r) => r.size).map((r) => (
                    <tr key={r.cohort}>
                      <td className="is-title">{periodLabel(r.cohort, d.grain)}</td><td className="r"><Num v={r.size} /></td>
                      {r.ftd!.within.map((w) => <WindowTd key={w.days} w={w} />)}
                      <td className="r"><Rate v={r.ftd!.toDate.pct} /> <span className="muted">({r.ftd!.toDate.n})</span></td>
                      <td className="r"><Hours v={r.ftd!.medianHours ?? null} /></td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr>
                    <td>Cả khoảng</td><td className="r"><Num v={d.total.size} /></td>
                    {d.total.ftd.within.map((w) => <WindowTd key={w.days} w={w} />)}
                    <td className="r"><Rate v={d.total.ftd.toDate.pct} /> <span className="muted">({d.total.ftd.toDate.n})</span></td><td />
                  </tr></tfoot>
                </table>
              </div>
              <p className="muted">Ngày đầu là ngày đăng ký. Mẫu số của mỗi cột chỉ gồm người đã hết khoảng đó (không tính người đăng ký gần đây). Nhóm theo {GRAIN_LABEL[d.grain].toLowerCase()} đăng ký.</p>
            </Panel>
          ) : null}
        </>
      )}
    </div>
  );
}
